import type { TFunction } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  FileDown,
  Loader2,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { StatusTransitionSheet } from './StatusTransitionSheet';
import { ConfirmDialog } from './ConfirmDialog';
import { useDebounce } from '../hooks/useDebounce';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import {
  useBulkDeleteQuotes,
  useDeleteQuote,
  useQuotes,
  type ConfidenceFilter,
} from '../api/hooks';
import { groupQuotesByDate } from '../utils/groupQuotesByDate';
import { statusBadgeProps } from '@/lib/statusTransitions';
import type { QuoteListItem, QuoteStatus } from '../api/types';
import type { GroupedQuotes } from '../utils/groupQuotesByDate';

interface QuoteDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (quoteId: string) => void;
  onDelete: (quoteId: string) => void;
  mode?: 'drawer' | 'sidebar';
}

function loadGroupMode(): 'date' | 'status' {
  try {
    const stored = globalThis.localStorage.getItem('voicequote-quote-group');
    return stored === 'status' ? 'status' : 'date';
  } catch {
    return 'date';
  }
}

function groupQuotesByStatus(quotes: QuoteListItem[]): Record<QuoteStatus, QuoteListItem[]> {
  const groups: Record<QuoteStatus, QuoteListItem[]> = {
    draft: [],
    sent: [],
    accepted: [],
    rejected: [],
    invoiced: [],
    paid: [],
  };
  for (const quote of quotes) {
    const bucket = groups[quote.status];
    if (bucket) {
      bucket.push(quote);
    }
  }
  return groups;
}

const STATUS_ORDER: QuoteStatus[] = ['draft', 'sent', 'accepted', 'rejected', 'invoiced', 'paid'];

function formatRelativeDate(dateStr: string, t: TFunction, locale: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 1) return t('time.justNow');
  if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
  if (diffHours < 48) return t('time.yesterday');
  return date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
}

function confidenceBadgeClass(score: number): string {
  if (score >= 80) return 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400';
  if (score >= 50)
    return 'border-yellow-500/30 bg-yellow-500/10 text-yellow-700 dark:text-yellow-400';
  return 'border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-400';
}

interface QuoteCardProps {
  readonly quote: QuoteListItem;
  readonly isExpanded: boolean;
  readonly isSelecting: boolean;
  readonly isSelected: boolean;
  readonly onToggle: (quoteId: string) => void;
  readonly onEdit: (quoteId: string) => void;
  readonly onDeleteClick: (e: React.MouseEvent, quoteId: string) => void;
  readonly onSelectToggle: (quoteId: string) => void;
  readonly onStatusChipClick: (quoteId: string, status: QuoteStatus) => void;
  readonly t: TFunction;
  readonly locale: string;
}

function QuoteCard({
  quote,
  isExpanded,
  isSelecting,
  isSelected,
  onToggle,
  onEdit,
  onDeleteClick,
  onSelectToggle,
  onStatusChipClick,
  t,
  locale,
}: QuoteCardProps) {
  const panelId = `quote-preview-${quote.id}`;

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(quote.id);
  };

  const handleClick = () => {
    if (isSelecting) {
      onSelectToggle(quote.id);
    } else {
      onToggle(quote.id);
    }
  };

  return (
    <Card className={`overflow-hidden ${isSelecting && isSelected ? 'ring-2 ring-primary' : ''}`}>
      <CardContent className="p-0">
        <div className="flex items-stretch">
          <button
            type="button"
            data-testid="quote-row"
            aria-expanded={isSelecting ? undefined : isExpanded}
            aria-controls={isSelecting ? undefined : panelId}
            aria-pressed={isSelecting ? isSelected : undefined}
            className="min-h-[44px] flex-1 cursor-pointer px-3 py-2 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={handleClick}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isSelecting && (
                  <div
                    data-testid="select-indicator"
                    className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 ${
                      isSelected
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-muted-foreground'
                    }`}
                  >
                    {isSelected && <Check className="h-3 w-3" />}
                  </div>
                )}
                <div>
                  <p className="font-medium">{quote.client_name || t('quote.untitled')}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatRelativeDate(quote.created_at, t, locale)}
                  </p>
                </div>
              </div>
              <div className="mr-1 flex items-center gap-2">
                <Badge variant="outline" className={confidenceBadgeClass(quote.confidence_score)}>
                  {quote.confidence_score}%
                </Badge>
                <p className="font-semibold">&euro;{(quote.grand_total ?? 0).toFixed(2)}</p>
              </div>
            </div>
          </button>
          {/* Status chip — sibling of card button, not nested inside it */}
          {!isSelecting && (
            <button
              type="button"
              data-testid={`status-chip-${quote.id}`}
              aria-haspopup="dialog"
              aria-label={t(`quoteStatus.${quote.status}`)}
              className="flex shrink-0 items-center self-center px-1 min-h-[44px]"
              onClick={() => onStatusChipClick(quote.id, quote.status)}
            >
              <Badge {...statusBadgeProps(quote.status)}>
                {t(`quoteStatus.${quote.status}`)}
                <ChevronDown className="ml-1 h-3 w-3" />
              </Badge>
            </button>
          )}
          {!isSelecting && (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => onDeleteClick(e, quote.id)}
              aria-label={t('quotes.delete')}
              className="mr-1 h-auto w-10 shrink-0 self-center text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardContent>
      {!isSelecting && (
        <section
          id={panelId}
          aria-label={t('quotes.previewLabel')}
          className="overflow-hidden motion-safe:transition-all motion-safe:duration-200"
          style={{ height: isExpanded ? 'auto' : '0px' }}
        >
          {isExpanded && (
            <div className="border-t border-border px-3 pb-3 pt-3">
              <p className="text-sm font-medium">
                {t('quote.grandTotal')}: &euro;{(quote.grand_total ?? 0).toFixed(2)}
              </p>
              <div className="mt-2 flex justify-end">
                <Button variant="outline" size="sm" onClick={handleEdit}>
                  {t('quotes.editQuote')}
                </Button>
              </div>
            </div>
          )}
        </section>
      )}
    </Card>
  );
}

interface SectionProps {
  readonly title: string;
  readonly quotes: ReadonlyArray<QuoteListItem>;
  readonly expandedId: string | null;
  readonly isSelecting: boolean;
  readonly selectedIds: ReadonlySet<string>;
  readonly onToggle: (quoteId: string) => void;
  readonly onEdit: (quoteId: string) => void;
  readonly onDeleteClick: (e: React.MouseEvent, quoteId: string) => void;
  readonly onSelectToggle: (quoteId: string) => void;
  readonly onStatusChipClick: (quoteId: string, status: QuoteStatus) => void;
  readonly t: TFunction;
  readonly locale: string;
}

function QuoteSection({
  title,
  quotes,
  expandedId,
  isSelecting,
  selectedIds,
  onToggle,
  onEdit,
  onDeleteClick,
  onSelectToggle,
  onStatusChipClick,
  t,
  locale,
}: SectionProps) {
  if (quotes.length === 0) return null;

  return (
    <div>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <div className="space-y-2">
        {quotes.map((quote) => (
          <QuoteCard
            key={quote.id}
            quote={quote}
            isExpanded={expandedId === quote.id}
            isSelecting={isSelecting}
            isSelected={selectedIds.has(quote.id)}
            onToggle={onToggle}
            onEdit={onEdit}
            onDeleteClick={onDeleteClick}
            onSelectToggle={onSelectToggle}
            onStatusChipClick={onStatusChipClick}
            t={t}
            locale={locale}
          />
        ))}
      </div>
    </div>
  );
}

type FilterKey = 'all' | 'high' | 'medium' | 'low';
type StatusFilterKey = 'all' | 'draft' | 'sent' | 'accepted' | 'rejected' | 'invoiced' | 'paid';

const CONFIDENCE_FILTERS: Record<FilterKey, ConfidenceFilter | undefined> = {
  all: undefined,
  high: { min_confidence: 80 },
  medium: { min_confidence: 50, max_confidence: 79 },
  low: { max_confidence: 49 },
};

interface GroupToggleProps {
  readonly groupMode: 'date' | 'status';
  readonly onSetDate: () => void;
  readonly onSetStatus: () => void;
  readonly t: TFunction;
}

function GroupToggle({ groupMode, onSetDate, onSetStatus, t }: GroupToggleProps) {
  return (
    <div className="flex gap-1 border-b border-border px-4 py-2" data-testid="group-toggle">
      <button
        type="button"
        data-testid="group-by-date"
        className={`min-h-[44px] rounded-full px-3 py-1 text-xs ${
          groupMode === 'date'
            ? 'bg-primary text-primary-foreground'
            : 'bg-secondary text-secondary-foreground'
        }`}
        onClick={onSetDate}
      >
        {t('quotes.groupByDate')}
      </button>
      <button
        type="button"
        data-testid="group-by-status"
        className={`min-h-[44px] rounded-full px-3 py-1 text-xs ${
          groupMode === 'status'
            ? 'bg-primary text-primary-foreground'
            : 'bg-secondary text-secondary-foreground'
        }`}
        onClick={onSetStatus}
      >
        {t('quotes.groupByStatus')}
      </button>
    </div>
  );
}

interface ActiveFilterChipsProps {
  readonly activeFilter: FilterKey;
  readonly activeStatusFilter: StatusFilterKey;
  readonly onClearConfidence: () => void;
  readonly onClearStatus: () => void;
  readonly t: TFunction;
}

function ActiveFilterChips({
  activeFilter,
  activeStatusFilter,
  onClearConfidence,
  onClearStatus,
  t,
}: ActiveFilterChipsProps) {
  return (
    <div className="flex flex-wrap gap-2 border-b border-border px-4 py-2">
      {activeFilter !== 'all' && (
        <Badge
          variant="secondary"
          className="flex min-h-[44px] cursor-pointer items-center gap-1 px-3"
          data-testid={`active-filter-chip-${activeFilter}`}
          onClick={onClearConfidence}
        >
          {t(`quotes.filter.${activeFilter}`)} <X className="h-3 w-3" />
        </Badge>
      )}
      {activeStatusFilter !== 'all' && (
        <Badge
          variant="secondary"
          className="flex min-h-[44px] cursor-pointer items-center gap-1 px-3"
          data-testid={`active-filter-chip-${activeStatusFilter}`}
          onClick={onClearStatus}
        >
          {t(`quoteStatus.${activeStatusFilter}`)} <X className="h-3 w-3" />
        </Badge>
      )}
    </div>
  );
}

export function QuoteDrawer({
  isOpen,
  onClose,
  onSelect,
  onDelete,
  mode = 'drawer',
}: Readonly<QuoteDrawerProps>) {
  const { t, i18n } = useTranslation();
  const { getAccessToken } = useKindeAuth();

  useEffect(() => {
    if (mode !== 'drawer') return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't close if a nested dialog/sheet handled the Escape already
      if (e.key === 'Escape' && isOpen && !e.defaultPrevented) {
        // Check if a Radix dialog/sheet/alertdialog is open (they add [data-state="open"] overlays)
        const nestedDialog = document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
        );
        if (!nestedDialog) onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, mode]);
  const [sort, setSort] = useState<'newest' | 'oldest'>('newest');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterKey>('all');
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [activeStatusFilter, setActiveStatusFilter] = useState<StatusFilterKey>('all');
  const [groupMode, setGroupMode] = useState(loadGroupMode);
  const [searchInput, setSearchInput] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusSheetQuote, setStatusSheetQuote] = useState<{
    id: string;
    status: QuoteStatus;
  } | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [bulkDeleteConfirmOpen, setBulkDeleteConfirmOpen] = useState(false);
  const searchQuery = useDebounce(searchInput, 300);
  const hasActiveFilters = activeFilter !== 'all' || activeStatusFilter !== 'all';
  const statusFilterValue = activeStatusFilter === 'all' ? undefined : activeStatusFilter;
  const { data, isLoading, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useQuotes(
      getAccessToken as () => Promise<string>,
      sort,
      CONFIDENCE_FILTERS[activeFilter],
      searchQuery || undefined,
      statusFilterValue,
    );
  const deleteQuote = useDeleteQuote(getAccessToken as () => Promise<string>);
  const bulkDelete = useBulkDeleteQuotes(getAccessToken as () => Promise<string>);
  const queryClient = useQueryClient();
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!sentinelRef.current || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleDelete = (e: React.MouseEvent, quoteId: string) => {
    e.stopPropagation();
    setDeleteConfirmId(quoteId);
  };

  const handleDeleteConfirmed = () => {
    /* istanbul ignore next -- @preserve: deleteConfirmId is always set when dialog is open */
    if (!deleteConfirmId) return;
    const quoteId = deleteConfirmId;
    deleteQuote.mutate(quoteId, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
        onDelete(quoteId);
      },
    });
  };

  const handleToggleExpand = (quoteId: string) => {
    setExpandedId((prev) => (prev === quoteId ? null : quoteId));
  };

  const handleToggleSort = () => {
    setSort((prev) => (prev === 'newest' ? 'oldest' : 'newest'));
    setExpandedId(null);
  };

  const handleToggleSelect = useCallback((quoteId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(quoteId)) {
        next.delete(quoteId);
      } else {
        next.add(quoteId);
      }
      return next;
    });
  }, []);

  const handleEnterSelectMode = () => {
    setIsSelecting(true);
    setExpandedId(null);
  };

  const handleExitSelectMode = () => {
    setIsSelecting(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = () => {
    setBulkDeleteConfirmOpen(true);
  };

  const handleBulkDeleteConfirmed = () => {
    const ids = [...selectedIds];
    bulkDelete.mutate(ids, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['quotes'] });
        ids.forEach((id) => onDelete(id));
        setSelectedIds(new Set());
        setIsSelecting(false);
      },
    });
  };

  const handleStatusChipClick = useCallback((quoteId: string, status: QuoteStatus) => {
    setStatusSheetQuote({ id: quoteId, status });
  }, []);

  const handleBulkExportPdf = () => {
    const ids = [...selectedIds];
    ids.forEach((id) => onSelect(id));
    setSelectedIds(new Set());
    setIsSelecting(false);
  };

  if (!isOpen) return null;

  const allQuotes = data?.pages.flatMap((page) => page.quotes) ?? [];
  const grouped: GroupedQuotes = groupQuotesByDate(allQuotes);
  const statusGrouped = groupQuotesByStatus(allQuotes);
  const allSelected = allQuotes.length > 0 && allQuotes.every((q) => selectedIds.has(q.id));

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allQuotes.map((q) => q.id)));
    }
  };

  const sections: Array<{ key: keyof GroupedQuotes; titleKey: string }> = [
    { key: 'today', titleKey: 'quotes.group.today' },
    { key: 'yesterday', titleKey: 'quotes.group.yesterday' },
    { key: 'thisWeek', titleKey: 'quotes.group.thisWeek' },
    { key: 'earlier', titleKey: 'quotes.group.earlier' },
  ];

  const isSidebar = mode === 'sidebar';

  const panelContent = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="text-lg font-semibold">{t('quotes.title')}</h2>
        <div className="flex items-center gap-1">
          {!isSelecting && allQuotes.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleEnterSelectMode}
              data-testid="select-mode-btn"
              className="min-h-[44px] text-xs text-muted-foreground"
            >
              {t('quotes.select')}
            </Button>
          )}
          {isSelecting && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExitSelectMode}
              data-testid="cancel-select-btn"
              className="min-h-[44px] text-xs text-muted-foreground"
            >
              {t('quotes.cancel')}
            </Button>
          )}
          {!isSidebar && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label={t('quotes.closeDrawer')}
              className="min-h-[44px]"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      {!isSelecting && (
        <div className="border-b border-border px-4 py-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              data-testid="search-input"
              placeholder={t('quotes.searchPlaceholder')}
              aria-label={t('quotes.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-9 w-full rounded-md border border-input bg-transparent pl-9 pr-9 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {searchInput && (
              <button
                type="button"
                data-testid="clear-search-btn"
                onClick={() => setSearchInput('')}
                aria-label={t('quotes.clearSearch')}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Sort + Filters bar */}
      {!isSelecting && (
        <div className="flex items-center gap-2 border-b border-border px-4 py-2">
          <Button
            variant={sort === 'newest' ? 'default' : 'outline'}
            size="sm"
            className="h-9 min-h-[44px] gap-1.5 text-xs"
            onClick={handleToggleSort}
            aria-label={sort === 'newest' ? t('quotes.sortNewest') : t('quotes.sortOldest')}
          >
            {sort === 'newest' ? (
              <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
            ) : (
              <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            <span>{sort === 'newest' ? t('quotes.sortNewest') : t('quotes.sortOldest')}</span>
          </Button>
          {mode === 'drawer' && (
            <Button
              variant={filtersOpen ? 'default' : 'outline'}
              size="sm"
              className="h-9 min-h-[44px] text-xs"
              onClick={() => setFiltersOpen((prev) => !prev)}
              aria-expanded={filtersOpen}
            >
              {t('quotes.filtersButton')}
            </Button>
          )}
        </div>
      )}

      {/* Active filter chips — shown in drawer mode when filters are collapsed and a filter is active */}
      {mode === 'drawer' && !filtersOpen && !isSelecting && hasActiveFilters && (
        <ActiveFilterChips
          activeFilter={activeFilter}
          activeStatusFilter={activeStatusFilter}
          onClearConfidence={() => {
            setActiveFilter('all');
            setExpandedId(null);
          }}
          onClearStatus={() => {
            setActiveStatusFilter('all');
            setExpandedId(null);
          }}
          t={t}
        />
      )}

      {/* Collapsible filter panel — always visible in sidebar mode, toggled in drawer mode */}
      {(mode === 'sidebar' || filtersOpen) && !isSelecting && (
        <>
          {/* Confidence Quick Chips */}
          <div className="flex flex-wrap gap-2 border-b border-border px-4 py-2">
            {(Object.keys(CONFIDENCE_FILTERS) as FilterKey[]).map((key) => (
              <Button
                key={key}
                variant={activeFilter === key ? 'default' : 'outline'}
                size="sm"
                className="min-h-[44px] text-xs"
                onClick={() => {
                  setActiveFilter(key);
                  setExpandedId(null);
                }}
                data-testid={`filter-chip-${key}`}
              >
                {t(`quotes.filter.${key}`)}
              </Button>
            ))}
          </div>

          {/* Group-by toggle (drawer only) */}
          {mode === 'drawer' && (
            <GroupToggle
              groupMode={groupMode}
              onSetDate={() => {
                setGroupMode('date');
                globalThis.localStorage.setItem('voicequote-quote-group', 'date');
              }}
              onSetStatus={() => {
                setGroupMode('status');
                globalThis.localStorage.setItem('voicequote-quote-group', 'status');
              }}
              t={t}
            />
          )}

          {/* Status Quick Chips */}
          <div className="flex flex-wrap gap-2 border-b border-border px-4 py-2">
            {(
              [
                'all',
                'draft',
                'sent',
                'accepted',
                'rejected',
                'invoiced',
                'paid',
              ] as StatusFilterKey[]
            ).map((key) => (
              <Button
                key={key}
                variant={activeStatusFilter === key ? 'default' : 'outline'}
                size="sm"
                className="min-h-[44px] text-xs"
                onClick={() => {
                  setActiveStatusFilter(key);
                  setExpandedId(null);
                }}
                data-testid={`status-filter-chip-${key}`}
              >
                {key === 'all' ? t('quoteStatus.filterAll') : t(`quoteStatus.${key}`)}
              </Button>
            ))}
          </div>
        </>
      )}

      {/* Select All bar (when in selection mode) */}
      {isSelecting && allQuotes.length > 0 && (
        <div className="flex items-center justify-between border-b border-border px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSelectAll}
            data-testid="select-all-btn"
            className="text-xs"
          >
            {allSelected ? t('quotes.deselectAll') : t('quotes.selectAll')}
          </Button>
          <span className="text-xs text-muted-foreground" data-testid="selected-count">
            {t('quotes.selectedCount', { count: selectedIds.size })}
          </span>
        </div>
      )}

      {/* Body */}
      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {/* Loading */}
        {isLoading && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                data-testid="quote-skeleton"
                className="h-16 animate-pulse rounded-lg bg-muted"
              />
            ))}
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">{t('error.loadQuotesFailed')}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
              {t('quotes.retry')}
            </Button>
          </div>
        )}

        {/* Empty */}
        {!isLoading && !isError && allQuotes.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            {searchQuery ? t('quotes.noResults') : t('quotes.empty')}
          </p>
        )}

        {/* Quote list — grouped by date or status */}
        {allQuotes.length > 0 && (
          <div className="space-y-4">
            {mode === 'drawer' && groupMode === 'status' ? (
              <>
                {STATUS_ORDER.map((status) => {
                  const statusQuotes = statusGrouped[status];
                  const badgeProps = statusBadgeProps(status);
                  return (
                    <div key={status}>
                      <div className="sticky top-0 z-10 mb-2 flex items-center gap-2 bg-card py-1">
                        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          {t(`quoteStatus.${status}`)}
                        </h3>
                        <Badge {...badgeProps} className={`text-xs ${badgeProps.className ?? ''}`}>
                          {statusQuotes.length}
                        </Badge>
                      </div>
                      {statusQuotes.length > 0 ? (
                        <div className="space-y-2">
                          {statusQuotes.map((quote) => (
                            <QuoteCard
                              key={quote.id}
                              quote={quote}
                              isExpanded={expandedId === quote.id}
                              isSelecting={isSelecting}
                              isSelected={selectedIds.has(quote.id)}
                              onToggle={handleToggleExpand}
                              onEdit={onSelect}
                              onDeleteClick={handleDelete}
                              onSelectToggle={handleToggleSelect}
                              onStatusChipClick={handleStatusChipClick}
                              t={t}
                              locale={i18n.language}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="py-2 text-center text-xs text-muted-foreground">
                          {t('quotes.emptyColumn')}
                        </p>
                      )}
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                {sections.map(({ key, titleKey }) => (
                  <QuoteSection
                    key={key}
                    title={t(titleKey)}
                    quotes={grouped[key]}
                    expandedId={expandedId}
                    isSelecting={isSelecting}
                    selectedIds={selectedIds}
                    onToggle={handleToggleExpand}
                    onEdit={onSelect}
                    onDeleteClick={handleDelete}
                    onSelectToggle={handleToggleSelect}
                    onStatusChipClick={handleStatusChipClick}
                    t={t}
                    locale={i18n.language}
                  />
                ))}
              </>
            )}

            {hasNextPage && <div ref={sentinelRef} className="h-1" />}
            {isFetchingNextPage && (
              <div className="flex justify-center py-2">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {deleteQuote.isError && (
          <p className="py-2 text-center text-sm text-destructive">{t('error.deleteFailed')}</p>
        )}
        {bulkDelete.isError && (
          <p className="py-2 text-center text-sm text-destructive">
            {t('quotes.bulkDeleteFailed')}
          </p>
        )}
      </div>

      {/* Sticky Action Bar (selection mode) */}
      {isSelecting && selectedIds.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="flex items-center justify-between border-t border-border bg-card px-4 py-3"
        >
          <span className="text-sm font-medium">
            {t('quotes.selectedCount', { count: selectedIds.size })}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkExportPdf}
              data-testid="bulk-export-btn"
              className="gap-1.5"
            >
              <FileDown className="h-4 w-4" />
              {t('quotes.bulkExportPdf')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              disabled={bulkDelete.isPending}
              data-testid="bulk-delete-btn"
              className="gap-1.5"
            >
              {bulkDelete.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {t('quotes.bulkDelete')}
            </Button>
          </div>
        </div>
      )}

      <StatusTransitionSheet
        quoteId={statusSheetQuote?.id ?? ''}
        currentStatus={statusSheetQuote?.status ?? 'draft'}
        isOpen={statusSheetQuote !== null}
        onClose={() => setStatusSheetQuote(null)}
        onStatusChanged={() => {
          setStatusSheetQuote(null);
        }}
      />

      <ConfirmDialog
        open={deleteConfirmId !== null}
        onOpenChange={
          /* istanbul ignore next -- @preserve: controlled component, open=true never emitted */ (
            open,
          ) => {
            if (!open) setDeleteConfirmId(null);
          }
        }
        title={t('quotes.confirmDelete')}
        description=""
        confirmLabel={t('quotes.delete')}
        cancelLabel={t('quotes.cancel')}
        onConfirm={handleDeleteConfirmed}
        variant="destructive"
      />

      <ConfirmDialog
        open={bulkDeleteConfirmOpen}
        onOpenChange={setBulkDeleteConfirmOpen}
        title={t('quotes.confirmBulkDelete', { count: selectedIds.size })}
        description=""
        confirmLabel={t('quotes.bulkDelete')}
        cancelLabel={t('quotes.cancel')}
        onConfirm={handleBulkDeleteConfirmed}
        variant="destructive"
      />
    </>
  );

  if (isSidebar) {
    return (
      <aside
        data-testid="quote-sidebar"
        className="sticky top-[57px] flex h-[calc(100vh-57px)] w-[440px] shrink-0 flex-col border-l border-border bg-card"
      >
        {panelContent}
      </aside>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        data-testid="drawer-backdrop"
        className="fixed inset-0 z-40 border-none bg-black/50"
        onClick={onClose}
        aria-label={t('quotes.closeDrawer')}
      />

      {/* Drawer */}
      <dialog
        open
        aria-label={t('quotes.title')}
        className="fixed inset-x-0 bottom-0 z-50 flex max-h-[70vh] flex-col rounded-t-2xl border-t border-border bg-card"
      >
        {panelContent}
      </dialog>
    </>
  );
}
